/**
 * Subscription Success Page
 * Universal-link return page for iOS Stripe checkout.
 * Attempts deep link to duolyst://premium/success, falls back to CTA.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, Sparkles, ArrowRight, Smartphone } from "lucide-react";

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [deepLinked, setDeepLinked] = useState(false);

  useEffect(() => {
    const verify = async () => {
      // Telemetry: return landed
      try {
        await axios.post(`${API}/subscription/telemetry`, {
          event: "stripe_checkout_return_success",
          metadata: { session_id: sessionId },
        });
      } catch (_) {}

      // Poll checkout status if we have a session ID
      if (sessionId) {
        try {
          const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            setStatus("success");
            // Refresh user to pick up new entitlement
            if (refreshUser) {
              await refreshUser();
              // Also trigger native app entitlement refresh
              window.dispatchEvent(new Event('duolyst:refresh-entitlements'));
              // Telemetry: premium unlocked
              try {
                await axios.post(`${API}/subscription/telemetry`, {
                  event: "premium_unlock_success",
                  metadata: { session_id: sessionId },
                });
              } catch (_) {}
            }
          } else {
            // Payment may still be processing via webhook
            setStatus("success");
          }
        } catch (_) {
          // Webhook may have already handled it
          setStatus("success");
        }
      } else {
        setStatus("success");
      }

      // Attempt deep link to app
      try {
        window.location.href = "duolyst://premium/success";
        setDeepLinked(true);
      } catch (_) {}
    };

    verify();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="subscription-success-page">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "verifying" ? (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
            <p className="text-muted-foreground">Verifying your subscription...</p>
          </div>
        ) : (
          <>
            {/* Success icon */}
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground" data-testid="success-heading">
                Welcome to Premium
              </h1>
              <p className="text-muted-foreground">
                Your subscription is active. All premium features are now unlocked.
              </p>
            </div>

            {/* Feature highlights */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                "Advanced analytics",
                "AI trip planner",
                "Unlimited photos",
                "Premium insights",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 p-2 rounded-lg bg-accent/50">
                  <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="text-foreground text-left">{f}</span>
                </div>
              ))}
            </div>

            {/* Deep link CTA for mobile users */}
            <div className="space-y-3 pt-2">
              <Button
                onClick={() => {
                  window.location.href = "duolyst://premium/success";
                }}
                className="w-full h-12 rounded-xl"
                data-testid="return-to-app-btn"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Return to DuoLyst
              </Button>

              {/* Web fallback */}
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="w-full"
                data-testid="go-to-dashboard-btn"
              >
                Continue on Web
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
